import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScanLine, Printer, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';

const STATE_BADGE = {
  outstanding: 'bg-amber-100 text-amber-700',
  unbilled:    'bg-red-100 text-red-700',
  billed:      'bg-green-100 text-green-700',
  box:         'bg-sky-100 text-sky-700',
  cancelled:   'bg-gray-200 text-gray-600',
};
const STATE_LABEL = {
  outstanding: 'Outstanding',
  unbilled:    'Unbilled',
  billed:      'Billed',
  // A sticker used as an extra box on another pickup. It carries no price of
  // its own — the parent consignment is billed once for the whole load.
  box:         'On a box',
  // Collected and then cancelled: the sticker is used up. (A parcel cancelled
  // before collection frees its sticker, which shows as Outstanding again.)
  cancelled:   'Cancelled',
};

export default function LabelTracking() {
  const [holder, setHolder] = useState('');   // driver id, 'office', or ''
  const [state,  setState]  = useState('');    // '', outstanding, unbilled, billed, box
  const [picked, setPicked] = useState({});
  const [busy,   setBusy]   = useState(false);
  const [search, setSearch] = useState('');
  const [from,   setFrom]   = useState('');
  const [to,     setTo]     = useState('');

  const isAdmin = can('labels.reprint');

  const { data: summary = [] } = useQuery({
    queryKey: ['barcode-summary'],
    queryFn: () => api.get('/barcodes/issued/summary').then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['barcode-issued', holder, state],
    queryFn: () => api.get('/barcodes/issued', {
      params: { driver_id: holder || undefined, state: state || undefined },
    }).then(r => r.data),
  });

  // Free-text filter over the number, holder and state
  const q = search.trim().toLowerCase();
  const rows = q
    ? allRows.filter(r =>
        r.tracking_number.toLowerCase().includes(q)
        || (r.holder_name || '').toLowerCase().includes(q)
        || (STATE_LABEL[r.state] || '').toLowerCase().includes(q))
    : allRows;

  const chosen = Object.keys(picked).filter((k) => picked[k]);
  const toggle = (t) => setPicked((p) => ({ ...p, [t]: !p[t] }));
  const shown = rows.map((r) => r.tracking_number);
  const allTicked = shown.length > 0 && shown.every((t) => picked[t]);

  // The number inside a barcode: OFF0091 → 91. Selecting "91 to 360" by hand
  // is 270 tick boxes, which is not a thing anyone should have to do.
  const seqOf = (t) => {
    const m = String(t).match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : null;
  };
  const selectRange = () => {
    const a = seqOf(from), b = seqOf(to);
    if (a == null || b == null) return toast.error('Enter a from and to number, e.g. 91 and 360');
    const lo = Math.min(a, b), hi = Math.max(a, b);
    // Only ever selects rows that are actually on screen, so the holder and
    // state filters still apply and nothing invisible gets reprinted.
    const hit = rows.filter(r => {
      const n = seqOf(r.tracking_number);
      return n != null && n >= lo && n <= hi;
    }).map(r => r.tracking_number);
    if (!hit.length) return toast.error(`Nothing on screen between ${lo} and ${hi}`);
    setPicked(p => ({ ...p, ...Object.fromEntries(hit.map(t => [t, true])) }));
    const prefixes = [...new Set(hit.map(t => t.replace(/\d+\s*$/, '')))];
    toast.success(`Selected ${hit.length} — ${prefixes.join(', ')} ${lo} to ${hi}`);
  };

  /* Reprint a damaged sticker. The number is NOT reissued — it keeps its holder
     and history — so this deliberately leaves a trail: the API is admin-only,
     audits every reprint and emails the office. */
  const reprint = async () => {
    if (!chosen.length) return toast.error('Tick the barcodes you need to reprint');
    const reason = window.prompt(
      `Reprint ${chosen.length} sticker${chosen.length === 1 ? '' : 's'}?\n\n`
      + 'This does not create new numbers — it prints the same ones again, so there will be\n'
      + 'two stickers per number. Destroy the damaged ones.\n\n'
      + 'Reason (goes in the log and the email):', 'Sticker paper damaged');
    if (reason === null) return;   // cancelled
    setBusy(true);
    try {
      const { data } = await api.post('/barcodes/reprint', {
        tracking_numbers: chosen, reason,
      });
      if (data.unknown?.length)
        toast.error(`Not issued, skipped: ${data.unknown.join(', ')}`);
      toast.success(data.notified.ok
        ? `${data.count} logged and the office was emailed — opening the print sheet`
        : `${data.count} logged, but the email did NOT go out (${data.notified.detail})`,
        { duration: data.notified.ok ? 4000 : 9000 });

      // Reuse the normal label sheet so the size and layout controls are the
      // same ones the office already knows. The numbers go through storage
      // rather than the query string — a 270-label range would be a ~2.5 kB
      // URL, which is asking for trouble.
      localStorage.setItem('reprint_batch', JSON.stringify(data.reprinted));
      window.open('/barcodes?reprint=batch', '_blank');
      setPicked({});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reprint those');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ScanLine size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Label Tracking</h1>
          <p className="text-xs text-gray-400">Reconcile the barcodes issued to each driver — spot outstanding &amp; unbilled stickers</p>
        </div>
      </div>

      {/* Per-holder summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {summary.map(s => (
          <div key={s.holder_key} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-gray-800">{s.holder_name}</span>
              <span className="text-xs text-gray-400">{s.issued} issued</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-green-600">{s.used}</p>
                <p className="text-[11px] text-gray-400">Used</p>
              </div>
              <div>
                <p className={`text-lg font-bold ${Number(s.outstanding) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{s.outstanding}</p>
                <p className="text-[11px] text-gray-400">Outstanding</p>
              </div>
              <div>
                <p className={`text-lg font-bold ${Number(s.unbilled) > 0 ? 'text-red-600' : 'text-gray-400'}`}>{s.unbilled}</p>
                <p className="text-[11px] text-gray-400">Unbilled</p>
              </div>
            </div>
          </div>
        ))}
        {summary.length === 0 && (
          <div className="text-sm text-gray-400 p-4">No labels issued yet. Issue some from the Barcodes page.</div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-3 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search barcode…"
            className="w-52 pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <select value={holder} onChange={e => setHolder(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
          <option value="">All holders</option>
          <option value="office">Office</option>
          {summary.filter(s => s.holder_key !== 'office').map(s => (
            <option key={s.holder_key} value={s.holder_key}>{s.holder_name}</option>
          ))}
        </select>
        <select value={state} onChange={e => setState(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
          <option value="">All states</option>
          <option value="outstanding">Outstanding</option>
          <option value="unbilled">Unbilled</option>
          <option value="billed">Billed</option>
          <option value="box">On a box</option>
        </select>

        <span className="text-xs text-gray-400 self-center">
          {rows.length}{rows.length !== allRows.length && ` of ${allRows.length}`} shown
        </span>
      </div>

      {/* Reprint — pick a range instead of ticking hundreds of boxes */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow-sm p-4 flex gap-3 flex-wrap items-center">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reprint</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Numbers</span>
            <input type="text" value={from} onChange={e => setFrom(e.target.value)}
              placeholder="91"
              className="w-20 border rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-xs text-gray-400">to</span>
            <input type="text" value={to} onChange={e => setTo(e.target.value)}
              placeholder="360"
              className="w-20 border rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <button onClick={selectRange}
              className="border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-2 text-sm text-gray-700">
              Select range
            </button>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {chosen.length > 0 && (
              <>
                <span className="text-sm font-semibold text-orange-600">{chosen.length} selected</span>
                <button onClick={() => setPicked({})}
                  className="text-xs text-gray-500 hover:text-red-500 underline">clear</button>
              </>
            )}
            <button onClick={reprint} disabled={busy || !chosen.length}
              className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-40">
              <Printer size={15} /> {busy ? 'Working…' : `Reprint ${chosen.length || 'selected'}`}
            </button>
          </div>
          <p className="w-full text-xs text-gray-400">
            Type the numbers off the stickers — <strong>91 to 360</strong> selects OFF0091–OFF0360.
            Only what is on screen is selected, so the holder, state and search filters still apply.
            You can also tick rows by hand, or use the header tick box to take everything shown.
          </p>
        </div>
      )}

      {isAdmin && chosen.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Reprinting does not create new numbers — the same ones print again, so there will be two
          stickers for each. Destroy the damaged ones. Every reprint is logged and emailed to the office.
        </p>
      )}

      {/* Barcode list */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No labels match</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  {isAdmin && (
                    <th className="px-4 py-3 w-8">
                      <input type="checkbox" checked={allTicked} className="accent-orange-500"
                        onChange={(e) => setPicked(e.target.checked
                          ? Object.fromEntries(shown.map((t) => [t, true])) : {})} />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left">Barcode</th>
                  <th className="px-4 py-3 text-left">Holder</th>
                  <th className="px-4 py-3 text-left">State</th>
                  <th className="px-4 py-3 text-left">Parcel status</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-left">Issued</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(r => (
                  <tr key={r.tracking_number}
                    className={`hover:bg-gray-50 ${picked[r.tracking_number] ? 'bg-orange-50' : ''}`}>
                    {isAdmin && (
                      <td className="px-4 py-2.5">
                        <input type="checkbox" checked={!!picked[r.tracking_number]}
                          onChange={() => toggle(r.tracking_number)} className="accent-orange-500" />
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-mono font-semibold text-gray-800">{r.tracking_number}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.holder_name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATE_BADGE[r.state]}`}>
                        {STATE_LABEL[r.state]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {r.state === 'box'
                        ? <span className="text-sky-700">box {r.piece_no} of {r.box_of}</span>
                        : (r.parcel_status || '—')}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">
                      {r.price != null ? `R${parseFloat(r.price).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">{new Date(r.issued_at).toLocaleDateString('en-ZA')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
